// status-renderer.ts
//
// `status [--watch] [--json]` — READ-ONLY: opens MissionStore for reading
// state, never acquires the machine/writer lock (test 37). Renders
// `<repo_root>/.avatar/status.html` atomically with mandatory HTML-escaping
// of all untrusted content (tests 38-39, no secret values ever rendered).
// `--watch` uses an INJECTABLE interval (default 5000ms per spec, test 40) —
// never a literal untestable setInterval.

import { redactKnownValuesDeep } from "./known-secret-values-deep-display-redaction.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import { openMissionStoreReadOnly } from "./mission-store-read-only.js";
import { readLimitsFromEffectiveConfig } from "./status-effective-config-readers.js";
// D12 (phase 05 B1): cost/operating-table payload shapes + I/O split out —
// see that module's header for why (this file sat at the 200-line ceiling).
import {
  buildStatusPayloadWithCost,
  buildStatusPayloadWithOperatingTable,
  type StatusPayloadWithCost,
  type StatusPayloadWithOperatingTable,
} from "./status-operating-table-snapshot.js";

export {
  buildStatusPayloadWithCost,
  buildStatusPayloadWithOperatingTable,
  type StatusPayloadWithCost,
  type StatusPayloadWithOperatingTable,
} from "./status-operating-table-snapshot.js";
export {
  buildStatusPayload,
  type ReadableMissionStore,
  type StatusPayload,
} from "./status-payload-types.js";

/** Read-only snapshot: open, build payload (incl. cost/limits, P13 Agent B), close. Never acquires the writer lock. Pass `missionWorkspace` to also populate the operating table (P13 Agent C) — omitted call sites (existing tests) keep the unchanged StatusPayloadWithCost shape. `missionIdFallback` (06f R3/F-8): the id the command already resolved, used when the journal never carried mission_started/mission_workspace_ready. */
export async function readStatusSnapshot(
  controlRoot: string,
  missionWorkspace?: string,
  missionIdFallback?: string,
): Promise<StatusPayloadWithCost | StatusPayloadWithOperatingTable> {
  // Bug H fix: was MissionStore.open() — the WRITER's EventJournal.open()
  // lease path, which threw JournalLeaseError for status's entire duration
  // whenever `run` held the lease (i.e. the exact moment an operator most
  // wants to check status). Lock-free reader; see mission-store-read-only.ts.
  const store = await openMissionStoreReadOnly(controlRoot);
  let payload: StatusPayloadWithCost | StatusPayloadWithOperatingTable;
  try {
    const limits = await readLimitsFromEffectiveConfig(controlRoot);
    const built = await buildStatusPayloadWithCost(store, limits);
    // Before the operating table: its mission line reads payload.mission_id.
    const withCost = { ...built, mission_id: built.mission_id ?? missionIdFallback };
    payload =
      missionWorkspace === undefined
        ? withCost
        : await buildStatusPayloadWithOperatingTable(withCost, controlRoot, missionWorkspace);
  } finally {
    await store.close();
  }
  // Phase 04a (I4a, N14): `status` là process khác `run` — nạp `test.env` qua
  // getter rồi che known-values TRƯỚC khi render text/`--json`/HTML.
  return redactKnownValuesDeep(payload, await knownValuesForControlRoot(controlRoot));
}

// HTML escaping/rendering/writing moved to status-renderer-html.ts (split,
// P13 Agent C — this file crossed 200 lines once the operating-table columns
// were added). Re-exported here so existing imports from status-renderer.js
// (`escapeHtml`, `renderStatusHtml`, `writeStatusHtml`) keep working.
export { escapeHtml, renderStatusHtml, writeStatusHtml } from "./status-renderer-html.js";

export interface WatchStatusOptions {
  intervalMs?: number;
  onSnapshot: (
    payload: StatusPayloadWithCost | StatusPayloadWithOperatingTable,
  ) => void | Promise<void>;
  /** Injectable timer so tests don't wait on real wall-clock time. */
  setIntervalFn?: (cb: () => void, ms: number) => unknown;
  clearIntervalFn?: (handle: unknown) => void;
}

export const DEFAULT_WATCH_INTERVAL_MS = 5000;

/**
 * Poll readStatusSnapshot on an injectable interval, calling onSnapshot each
 * time. Returns a stop() function. Never mutates state — watch is read-only
 * (test 40: "refreshes without mutating state"). `--watch` keeps polling on
 * the SAME `intervalMs` across refreshes (spec §15) — this function never
 * changes it mid-poll, callers just pass the same options object every time.
 */
export function watchStatus(
  controlRoot: string,
  options: WatchStatusOptions,
  missionWorkspace?: string,
  missionIdFallback?: string,
): { stop: () => void } {
  const intervalMs = options.intervalMs ?? DEFAULT_WATCH_INTERVAL_MS;
  const setIntervalFn = options.setIntervalFn ?? ((cb, ms) => setInterval(cb, ms));
  const clearIntervalFn = options.clearIntervalFn ?? ((h) => clearInterval(h as NodeJS.Timeout));

  const tick = () => {
    readStatusSnapshot(controlRoot, missionWorkspace, missionIdFallback)
      .then((payload) => options.onSnapshot(payload))
      .catch(() => {
        // Read-only watch: a transient read error is not fatal — next tick retries.
      });
  };

  // H18 (close-out 2026-09-30): show the status now, then refresh — the first
  // render used to wait a whole interval (5 s of blank screen).
  tick();
  const handle = setIntervalFn(tick, intervalMs);
  return { stop: () => clearIntervalFn(handle) };
}
